/**
 * ComingSoonTab — DRY placeholder for "Coming Soon" stub tabs in ManagePlatform.
 * Pass `variant` to pick the right copy + icon + back behaviour.
 * Fully bilingual — pass `language` ('ar' | 'en').
 */
import {
  ClipboardList, FileText, Eye, TrendingUp, Phone, Car, Hash, CreditCard, UserPlus,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/button';

const VARIANTS = {
  'subscriptions': {
    icon: CreditCard, color: 'text-amber-500',
    titleAr: 'إدارة الاشتراكات', titleEn: 'Subscriptions Management',
    bodyAr: 'نظام الاشتراكات', bodyEn: 'Subscriptions System',
    subAr: 'قريباً - سيتم إضافة نظام إدارة اشتراكات التجار', subEn: 'Coming soon — merchant subscriptions management will be added here',
    hasBack: false, withCard: false,
  },
  'agent-tasks-hub': {
    icon: ClipboardList, color: 'text-orange-500',
    titleAr: 'مهام الوكلاء', titleEn: 'Agent Tasks',
    backTo: 'main',
    hasBack: true, withCard: true,
    cards: [
      // ── Ready-made tasks (self-served by agents on the sub-platform) ──
      { tab: 'add-agent-task', icon: FileText, hover: 'hover:border-blue-500', iconBg: 'bg-blue-500/20', iconColor: 'text-blue-500', titleAr: 'إدراج مهام جاهزة للوكلاء', titleEn: 'Create Ready-Made Agent Tasks', descAr: 'بطاقات جاهزة يختارها الوكيل بنفسه من موقع التجار', descEn: 'Ready cards the agent picks himself from the merchants site' },
      // ── Manual task (added Feb 2026) — for agents that reach out on WhatsApp
      //    and don't submit through the sub-platform themselves ──
      { externalPath: '/dashboard/cloudgm/assigned-tasks/new?target=agent', icon: UserPlus, hover: 'hover:border-purple-500', iconBg: 'bg-purple-500/20', iconColor: 'text-purple-500', titleAr: 'إدراج مهمة لوكيل (يدوي)', titleEn: 'Add Manual Agent Task', descAr: 'كلمك الوكيل على الواتساب/الهاتف — أدخل له المهمة يدوياً بدلاً منه', descEn: 'The agent contacted you via WhatsApp/phone — log the task on their behalf', testid: 'coming-soon-card-add-manual-agent-task' },
      // ── Unified follow-up (manual + web + AI-generated) ──
      { tab: 'follow-agent-tasks', icon: Eye, hover: 'hover:border-emerald-500', iconBg: 'bg-emerald-500/20', iconColor: 'text-emerald-500', titleAr: 'متابعة مهام الوكلاء', titleEn: 'Track Agent Tasks', descAr: 'عرض جميع المهام (يدوية، من الموقع، ومن الذكاء الاصطناعي)', descEn: 'View all tasks (manual, from the site, and AI-generated)' },
    ],
  },
  'add-agent-task': {
    icon: FileText, color: 'text-blue-500',
    titleAr: 'إدراج مهام للوكلاء', titleEn: 'Create Agent Tasks',
    backTo: 'agent-tasks', hasBack: true,
    bodyAr: 'قريباً', bodyEn: 'Coming Soon',
    subAr: 'سيتم إضافة نموذج إدراج المهام هنا', subEn: 'The task creation form will be added here',
  },
  'follow-agent-tasks': {
    icon: Eye, color: 'text-emerald-500',
    titleAr: 'متابعة مهام الوكلاء', titleEn: 'Track Agent Tasks',
    backTo: 'agent-tasks', hasBack: true,
    bodyAr: 'لا توجد مهام حالياً', bodyEn: 'No tasks yet',
    subAr: 'ستظهر هنا مهام الوكلاء للمتابعة', subEn: 'Agent tasks will appear here for tracking',
  },
  'market-follow-hub': {
    icon: TrendingUp, color: 'text-teal-500',
    titleAr: 'متابعة السوق', titleEn: 'Market Follow',
    backTo: 'main', hasBack: true, withCard: true,
    cards: [
      { tab: 'market-phone-numbers', icon: Phone, hover: 'hover:border-blue-500', iconBg: 'bg-blue-500/20', iconColor: 'text-blue-500', titleAr: 'أرقام الهواتف', titleEn: 'Phone Numbers', descAr: 'متابعة إعلانات أرقام الهواتف', descEn: 'Track phone number listings' },
      { tab: 'market-cars', icon: Car, hover: 'hover:border-emerald-500', iconBg: 'bg-emerald-500/20', iconColor: 'text-emerald-500', titleAr: 'السيارات', titleEn: 'Cars', descAr: 'متابعة إعلانات السيارات', descEn: 'Track car listings' },
      { tab: 'market-car-plates', icon: Hash, hover: 'hover:border-amber-500', iconBg: 'bg-amber-500/20', iconColor: 'text-amber-500', titleAr: 'أرقام السيارات', titleEn: 'Car Plates', descAr: 'متابعة إعلانات لوحات السيارات', descEn: 'Track car plate listings' },
    ],
  },
  'market-cars': {
    icon: Car, color: 'text-emerald-500',
    titleAr: 'السيارات', titleEn: 'Cars',
    backTo: 'market-follow', hasBack: true,
    bodyAr: 'لا توجد إعلانات حالياً', bodyEn: 'No listings yet',
    subAr: 'ستظهر هنا إعلانات السيارات للمتابعة', subEn: 'Car listings will appear here',
  },
  'market-car-plates': {
    icon: Hash, color: 'text-amber-500',
    titleAr: 'أرقام السيارات (اللوحات)', titleEn: 'Car Plates',
    backTo: 'market-follow', hasBack: true,
    bodyAr: 'لا توجد إعلانات حالياً', bodyEn: 'No listings yet',
    subAr: 'ستظهر هنا إعلانات لوحات السيارات للمتابعة', subEn: 'Car plate listings will appear here',
  },
};

export default function ComingSoonTab({ variant, setActiveTab, language = 'ar' }) {
  const config = VARIANTS[variant];
  const navigate = useNavigate();
  if (!config) return null;

  const isAr = language === 'ar';
  const Icon = config.icon;
  const title = isAr ? config.titleAr : (config.titleEn || config.titleAr);

  // Hub variant — show navigation cards (e.g. agent-tasks-hub, market-follow-hub)
  if (config.withCard && config.cards) {
    return (
      <div data-testid={`coming-soon-${variant}`}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Icon className={`h-6 w-6 ${config.color}`} />
            {title}
          </h2>
          {config.hasBack && (
            <Button
              onClick={() => setActiveTab(config.backTo)}
              variant="outline"
              className="border-[#2a2a4e] text-white hover:bg-[#2a2a4e] rounded-xl"
              data-testid={`coming-soon-back-${variant}`}
            >
              {isAr ? 'رجوع' : 'Back'}
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {config.cards.map((card) => {
            const CardIcon = card.icon;
            // Cards can EITHER switch to an inner tab (card.tab) OR jump to an
            // external route in the app (card.externalPath — used e.g. for the
            // "manual agent task" card that opens the shared task-create page).
            const handleClick = () => {
              if (card.externalPath) navigate(card.externalPath);
              else setActiveTab(card.tab);
            };
            const cardKey = card.tab || card.externalPath;
            const cardTestid = card.testid || `coming-soon-card-${card.tab}`;
            return (
              <div
                key={cardKey}
                onClick={handleClick}
                className={`group bg-[#1a1a2e] rounded-2xl p-8 border border-[#2a2a4e] ${card.hover} transition-all cursor-pointer hover:-translate-y-1`}
                data-testid={cardTestid}
              >
                <div className="text-center">
                  <div className={`w-16 h-16 ${card.iconBg} rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform`}>
                    <CardIcon className={`h-8 w-8 ${card.iconColor}`} />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{isAr ? card.titleAr : (card.titleEn || card.titleAr)}</h3>
                  <p className="text-sm text-[#808080]">{isAr ? card.descAr : (card.descEn || card.descAr)}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Simple "Coming Soon" placeholder
  return (
    <div data-testid={`coming-soon-${variant}`}>
      {config.hasBack ? (
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Icon className={`h-6 w-6 ${config.color}`} />
            {title}
          </h2>
          <Button
            onClick={() => setActiveTab(config.backTo)}
            variant="outline"
            className="border-[#2a2a4e] text-white hover:bg-[#2a2a4e] rounded-xl"
            data-testid={`coming-soon-back-${variant}`}
          >
            {isAr ? 'رجوع' : 'Back'}
          </Button>
        </div>
      ) : (
        <h2 className="text-xl font-bold text-white mb-5">{title}</h2>
      )}

      <div className="text-center py-16 text-[#808080] bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e]">
        <Icon className={`h-16 w-16 mx-auto mb-4 opacity-30 ${config.color}`} />
        <p className="text-lg">{isAr ? config.bodyAr : (config.bodyEn || config.bodyAr)}</p>
        <p className="text-sm mt-2 text-[#606060]">{isAr ? config.subAr : (config.subEn || config.subAr)}</p>
      </div>
    </div>
  );
}
